// PAYEE-DIRECTORY-1 — payee accounting profile editor (Directory surface).
// Loads/edits the payee_profiles row for a canonical entity (company|contact).
// TIN is write-only here: the API never returns the raw value, only tin_last4.
import { useState, useEffect } from "react";
import { apiFetch, can } from "../api";
import { KeyValue } from "./ui";

const W9_OPTS = [
  ["unknown_review", "Unknown / needs review"],
  ["required", "W9 required"],
  ["not_required", "W9 not required"],
  ["exempt", "Exempt"],
  ["foreign_future", "Foreign (future)"],
];
const MECH_OPTS = ["", "Check", "ACH", "Zelle"];

// LD-DESIGN-012 pattern 2 (entity-profile anatomy): the modal's read summary is
// Profile + Payment Readiness + Quick Facts, stacked to fit the modal width.
// Everything below (vetting banner, QBO panel, edit form) is unchanged.
function Block({ title, right, children }) {
  return (
    <div style={{ marginTop: 10, padding: "9px 11px", borderRadius: 6, border: "1px solid var(--border,#DDE2E8)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>{title}</span>{right}
      </div>
      {children}
    </div>
  );
}

function PayeeReadSummary({ rdy, form, tin4, vettedBy, kind }) {
  const address = [form.remit_address_line1, form.remit_address_line2,
    [form.remit_city, form.remit_state].filter(Boolean).join(", "), form.remit_zip].filter(Boolean).join(", ");
  // Honesty rule: every check derives from server-computed readiness flags the
  // /payees/entity payload already carries (backend/payee_api.py _readiness).
  const checks = [
    { key: "w9", label: "W-9 on file", ok: !!rdy.w9_ok },
    { key: "tin", label: "TIN on file", ok: !!rdy.tin_on_file },
    { key: "ofac", label: "OFAC clear", ok: !!rdy.ofac_ok },
    { key: "addr", label: "Remit address complete", ok: !!rdy.address_ok },
    { key: "legal", label: "Legal name recorded", ok: !!rdy.legal_name_ok },
    { key: "pref", label: "Payment preference set", ok: !!rdy.payment_pref_ok },
    { key: "qbo", label: "QBO vendor linked", ok: !!rdy.qbo_vendor_ok },
    { key: "vet", label: "Vetting approved", ok: !!rdy.vetted },
  ];
  const done = checks.filter(c => c.ok).length;
  const w9Label = (W9_OPTS.find(([v]) => v === form.w9_status) || [])[1];
  return (
    <>
      <Block title="Profile">
        <KeyValue label="Name on check">{form.display_name || null}</KeyValue>
        <KeyValue label="Tax / Vendor entity">{form.legal_name || null}</KeyValue>
        <KeyValue label="Type">{kind === "company" ? "Company" : "Contact"}</KeyValue>
        <KeyValue label="Remit attn">{form.remit_attn || null}</KeyValue>
        <KeyValue label="Remit address">{address || null}</KeyValue>
        <KeyValue label="TIN / EIN">{tin4 ? `••••${tin4}` : null}</KeyValue>
      </Block>
      <Block title="Payment Readiness" right={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {rdy.payment_hold && <span style={{ fontSize: 10, color: "#fff", background: "#dc2626", borderRadius: 4, padding: "1px 7px" }}>PAYMENT HOLD</span>}
          <span style={{ fontSize: 11, color: "var(--text2)" }}>{done}/{checks.length} complete</span>
        </span>}>
        <div style={{ height: 4, borderRadius: 2, background: "#e2e8f0", marginBottom: 8, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.round((done / checks.length) * 100)}%`,
                        background: done === checks.length ? "#16a34a" : "#d97706", transition: "width 0.2s" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
          {checks.map(ch => (
            <div key={ch.key} style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12 }}>
              <span style={{ color: ch.ok ? "#16a34a" : "#d97706", fontWeight: 700, flexShrink: 0, width: 13 }}>{ch.ok ? "✓" : "⚠"}</span>
              <span style={{ fontWeight: ch.ok ? 400 : 600 }}>{ch.label}</span>
            </div>
          ))}
        </div>
      </Block>
      <Block title="Quick Facts">
        <KeyValue label="1099 reportable">{form.reportable_1099 === true ? "Yes" : form.reportable_1099 === false ? "No" : null}</KeyValue>
        <KeyValue label="W-9 status">{w9Label || null}</KeyValue>
        <KeyValue label="Payment preference">{form.payment_preference || null}</KeyValue>
        <KeyValue label="OFAC result">{form.ofac_result || null}</KeyValue>
        <KeyValue label="Vetting">{form.vetting_status === "approved"
          ? `Approved${vettedBy ? ` · ${vettedBy}` : ""}${form.vetted_at ? ` · ${String(form.vetted_at).slice(0, 10)}` : ""}`
          : form.vetting_status === "rejected" ? "Rejected" : "Pending"}</KeyValue>
        {form.payment_hold && <KeyValue label="Hold reason">{form.payment_hold_reason || null}</KeyValue>}
      </Block>
    </>
  );
}

const lbl = { display: "block", fontSize: 11, color: "var(--text2,#555)", marginBottom: 3, marginTop: 8, fontWeight: 600 };
const inp = { width: "100%", padding: "6px 9px", fontSize: 13, border: "1px solid var(--border,#DDE2E8)", borderRadius: 6, boxSizing: "border-box" };

const QF_LABEL = { display_name: "Name on check", legal_name: "Tax entity", remit_address_line1: "Address",
  remit_city: "City", remit_state: "State", remit_zip: "Zip", reportable_1099: "1099", tin_last4: "TIN (last 4)" };
const qfShow = (v) => v === true ? "Yes" : v === false ? "No" : (v == null || v === "") ? "—" : String(v);

// QuickBooks vendor link + bidirectional sync. Suggest-only; a push back to QBO additionally needs
// qbo_vendor_config.autosync on the server (otherwise those fields report as skipped).
function QboVendorPanel({ kind, entityId, canManage, onChanged, refreshKey }) {
  const [st, setSt] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => apiFetch(`/payees/entity/${kind}/${entityId}/qbo-sync-status`)
    .then(d => { setSt(d); const r = {}; (d.diffs || []).forEach(x => { r[x.field] = "qbo"; }); setSel(r); })
    .catch(e => setMsg(String(e.message || e).slice(0, 160)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, entityId, refreshKey]);
  const search = () => { if (q.trim().length < 2) return; setBusy(true); apiFetch(`/payees/qbo-suggest?q=${encodeURIComponent(q.trim())}`).then(d => setResults(d.results || [])).catch(e => setMsg(String(e.message || e).slice(0, 160))).finally(() => setBusy(false)); };
  const link = (vid) => { setBusy(true); setMsg(null); apiFetch(`/payees/entity/${kind}/${entityId}/qbo-link`, { method: "POST", body: { qbo_vendor_id: vid } }).then(() => { setResults(null); setQ(""); return load(); }).then(() => { setMsg("Linked ✓"); onChanged && onChanged(); }).catch(e => setMsg(String(e.message || e).slice(0, 160))).finally(() => setBusy(false)); };
  const unlink = () => { if (!window.confirm("Unlink this payee from its QBO vendor?")) return; setBusy(true); apiFetch(`/payees/entity/${kind}/${entityId}/qbo-unlink`, { method: "POST" }).then(load).then(() => { setMsg("Unlinked"); onChanged && onChanged(); }).finally(() => setBusy(false)); };
  const authorize = () => { setBusy(true); setMsg(null); apiFetch(`/payees/entity/${kind}/${entityId}/qbo-sync`, { method: "POST", body: { resolutions: sel } }).then(r => { setMsg(`Synced — pulled ${r.pulled.length}, pushed ${r.pushed.length}${r.skipped.length ? `, skipped: ${r.skipped.join(", ")}` : ""}`); return load(); }).then(() => onChanged && onChanged()).catch(e => setMsg(String(e.message || e).slice(0, 180))).finally(() => setBusy(false)); };

  const linked = st && st.linked;
  return (
    <div style={{ marginTop: 10, padding: "9px 11px", borderRadius: 6, border: "1px solid var(--border,#DDE2E8)", background: "#f8fafc" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>QuickBooks vendor</span>
        {linked
          ? <span style={{ fontSize: 10, color: "#fff", background: "#16a34a", borderRadius: 4, padding: "1px 7px" }}>QBO Linked</span>
          : <span style={{ fontSize: 10, color: "#92400e", background: "#fef3c7", borderRadius: 4, padding: "1px 7px" }}>Not linked</span>}
        {linked && st.vendor_name && <span style={{ fontSize: 11, color: "var(--text2)" }}>{st.vendor_name}</span>}
        {linked && (st.in_sync
          ? <span style={{ fontSize: 11, color: "#166534", marginLeft: "auto" }}>in sync ✓</span>
          : <span style={{ fontSize: 11, color: "#b45309", marginLeft: "auto", fontWeight: 600 }}>out of sync — review</span>)}
      </div>
      {linked && st.error && <div style={{ fontSize: 11, color: "#991b1b", marginTop: 4 }}>{st.error}</div>}
      {refreshKey > 0 && linked && !st.in_sync && (
        <div style={{ marginTop: 8, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", borderRadius: 5, padding: "6px 9px", fontSize: 11.5 }}>
          ⚠ You changed this vendor in Atticus — it's now <b>out of sync</b> with QuickBooks. Review the differences and <b>Authorize</b> to push the change (or it'll flow on the next authorized sync).
        </div>
      )}

      {linked && !st.in_sync && (st.diffs || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: "var(--text3)" }}>
              <td style={{ padding: "2px 4px" }}>Field</td><td style={{ padding: "2px 4px" }}>Atticus</td><td style={{ padding: "2px 4px" }}>QuickBooks</td>
            </tr></thead>
            <tbody>
              {st.diffs.map(d => (
                <tr key={d.field} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "3px 4px", fontWeight: 600 }}>{QF_LABEL[d.field] || d.field}</td>
                  <td style={{ padding: "3px 4px" }}>
                    <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                      <input type="radio" disabled={!canManage} checked={sel[d.field] === "atticus"} onChange={() => setSel(s => ({ ...s, [d.field]: "atticus" }))} />{qfShow(d.atticus)}
                    </label>
                  </td>
                  <td style={{ padding: "3px 4px" }}>
                    <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                      <input type="radio" disabled={!canManage} checked={sel[d.field] === "qbo"} onChange={() => setSel(s => ({ ...s, [d.field]: "qbo" }))} />{qfShow(d.qbo)}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 3 }}>Pick the correct value for each. "QuickBooks" pulls into Atticus; "Atticus" pushes to QBO (needs sync enabled).</div>
          {canManage && <button className="btn btn-sm btn-blue" style={{ marginTop: 6 }} disabled={busy} onClick={authorize}>Authorize sync</button>}
        </div>
      )}

      {linked && canManage && <button className="btn btn-sm btn-outline" style={{ marginTop: 8 }} disabled={busy} onClick={unlink}>Unlink</button>}

      {!linked && canManage && (
        <div style={{ marginTop: 7 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input style={{ ...inp, marginTop: 0 }} placeholder="Search QuickBooks vendors by name…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()} />
            <button className="btn btn-sm btn-outline" disabled={busy} onClick={search}>Search</button>
          </div>
          {results && results.length === 0 && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 5 }}>No QBO vendor matches.</div>}
          {results && results.map(r => (
            <div key={r.qbo_vendor_id} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, fontSize: 11 }}>
              <div style={{ flex: 1 }}><b>{r.display_name}</b>{r.tin_last4 ? ` · TIN ••••${r.tin_last4}` : ""}{r.vendor1099 ? " · 1099" : ""}{r.address ? ` · ${r.address}` : ""}</div>
              <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => link(r.qbo_vendor_id)}>Link</button>
            </div>
          ))}
        </div>
      )}
      {msg && <div style={{ fontSize: 11, marginTop: 6, color: /✓|Synced|Linked/.test(msg) ? "#166534" : "#991b1b" }}>{msg}</div>}
    </div>
  );
}

export default function PayeeProfileModal({ kind, entityId, name, onClose }) {
  const [rdy, setRdy] = useState(null);
  const [form, setForm] = useState(null);
  const [tin4, setTin4] = useState(null);
  const [vettedBy, setVettedBy] = useState(null);
  const [hasW9, setHasW9] = useState(false);
  const [hasOfac, setHasOfac] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [syncTick, setSyncTick] = useState(0);   // bumps on save → QBO panel re-checks drift
  const canManage = can("manage_payees");

  const load = () => apiFetch(`/payees/entity/${kind}/${entityId}`)
    .then(d => { setRdy(d.readiness); setForm(d.profile || {}); setTin4(d.profile && d.profile.tin_last4); setVettedBy(d.vetted_by_name); setHasW9(d.has_w9_file); setHasOfac(d.has_ofac_file); })
    .catch(e => setMsg(String(e.message || e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, entityId]);

  const vet = (decision) => {
    if (!form || !form.id) { setMsg("Save the profile first."); return; }
    setBusy(true); setMsg(null);
    apiFetch(`/payees/${form.id}/vet`, { method: "POST", body: { decision } })
      .then(load).then(() => setMsg(decision === "approve" ? "Vetted ✓" : "Rejected")).catch(e => setMsg(String(e.message || e).slice(0, 200))).finally(() => setBusy(false));
  };
  const uploadW9 = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await apiFetch(`/payees/entity/${kind}/${entityId}/w9`, { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      setHasW9(true); setMsg("W-9 attached.");
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };
  const uploadOfac = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await apiFetch(`/payees/entity/${kind}/${entityId}/ofac`, { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      setHasOfac(true); setMsg("OFAC report attached.");
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const keys = ["legal_name", "display_name", "remit_attn", "remit_address_line1",
        "remit_address_line2", "remit_city", "remit_state", "remit_zip", "remit_country",
        "w9_status", "w9_doc_url", "payment_preference", "payment_instructions",
        "payment_hold_reason", "notes", "ofac_result"];
      const body = {};
      keys.forEach(k => { if (form[k] != null) body[k] = form[k]; });
      body.payment_hold = !!form.payment_hold;
      if (form.reportable_1099 === true || form.reportable_1099 === false) body.reportable_1099 = form.reportable_1099;
      if (form._tin) body.tin = form._tin;
      const d = await apiFetch(`/payees/entity/${kind}/${entityId}`, { method: "POST", body });
      setRdy(d.readiness); setForm({ ...(d.profile || {}), _tin: "" }); setTin4(d.profile && d.profile.tin_last4);
      setMsg("Saved.");
      setSyncTick(t => t + 1);   // re-check QBO drift → nudge to re-sync if this vendor is QBO-linked
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: 560, maxWidth: "100%", background: "#fff", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Payee · {name}</div>
            <div style={{ fontSize: 11, color: "var(--text3,#888)" }}>{kind} #{entityId}</div>
          </div>
          <button className="btn btn-sm btn-outline" onClick={onClose}>Close</button>
        </div>

        {rdy && form && <PayeeReadSummary rdy={rdy} form={form} tin4={tin4} vettedBy={vettedBy} kind={kind} />}

        {form && (
          <div style={{ marginTop: 10, padding: "7px 10px", borderRadius: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            background: form.vetting_status === "approved" ? "#dcfce7" : form.vetting_status === "rejected" ? "#fee2e2" : "#fef3c7",
            color: form.vetting_status === "approved" ? "#166534" : form.vetting_status === "rejected" ? "#991b1b" : "#92400e" }}>
            <b>Vetting: {form.vetting_status === "approved" ? "Approved" : form.vetting_status === "rejected" ? "Rejected" : "Pending"}</b>
            {form.vetting_status === "approved" && vettedBy && <span>· by {vettedBy}{form.vetted_at ? ` on ${String(form.vetted_at).slice(0, 10)}` : ""}</span>}
            {canManage && form.id && form.vetting_status !== "approved" && <button className="btn btn-sm btn-blue" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => vet("approve")}>Approve</button>}
            {canManage && form.id && form.vetting_status !== "rejected" && <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => vet("reject")}>Reject</button>}
          </div>
        )}

        {form !== null && <QboVendorPanel kind={kind} entityId={entityId} canManage={canManage} onChanged={load} refreshKey={syncTick} />}

        {form === null ? <div style={{ marginTop: 14 }}>Loading…</div> : (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lbl}>Tax / Vendor entity <span style={{ color: "var(--text3)", fontWeight: 400 }}>(on W-9)</span></label><input style={inp} value={form.legal_name || ""} onChange={e => up("legal_name", e.target.value)} disabled={!canManage} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Name on check <span style={{ color: "var(--text3)", fontWeight: 400 }}>(Payee)</span></label><input style={inp} value={form.display_name || ""} onChange={e => up("display_name", e.target.value)} disabled={!canManage} /></div>
            </div>
            <div style={{ marginTop: 8, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>W-9 document:</span>
              {hasW9 ? <a href={`/api/payees/entity/${kind}/${entityId}/w9`} target="_blank" rel="noreferrer">view on file ↗</a> : <span style={{ fontSize: 12, color: "var(--text3)" }}>none attached</span>}
              {canManage && <label className="btn btn-sm btn-outline" style={{ cursor: "pointer" }}>{hasW9 ? "Replace" : "Attach W-9"}<input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => uploadW9(e.target.files[0])} /></label>}
            </div>
            <div style={{ marginTop: 8, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>OFAC report:</span>
              {hasOfac ? <a href={`/api/payees/entity/${kind}/${entityId}/ofac`} target="_blank" rel="noreferrer">view on file ↗</a> : <span style={{ fontSize: 12, color: "var(--text3)" }}>none attached</span>}
              {canManage && <label className="btn btn-sm btn-outline" style={{ cursor: "pointer" }}>{hasOfac ? "Replace" : "Attach OFAC"}<input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => uploadOfac(e.target.files[0])} /></label>}
              <span style={{ fontSize: 12, color: "var(--text3)" }}>Result</span>
              <select style={{ ...inp, width: 120 }} value={form.ofac_result || ""} onChange={e => up("ofac_result", e.target.value)} disabled={!canManage}>
                <option value="">— not set —</option>
                <option value="clear">Clear</option>
                <option value="review">Review</option>
                <option value="hit">Hit</option>
              </select>
              {canManage && <span style={{ fontSize: 10, color: "var(--text3)" }}>(save to record)</span>}
            </div>
            <label style={lbl}>Remit attn</label><input style={inp} value={form.remit_attn || ""} onChange={e => up("remit_attn", e.target.value)} disabled={!canManage} />
            <label style={lbl}>Address line 1</label><input style={inp} value={form.remit_address_line1 || ""} onChange={e => up("remit_address_line1", e.target.value)} disabled={!canManage} />
            <label style={lbl}>Address line 2</label><input style={inp} value={form.remit_address_line2 || ""} onChange={e => up("remit_address_line2", e.target.value)} disabled={!canManage} />
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 2 }}><label style={lbl}>City</label><input style={inp} value={form.remit_city || ""} onChange={e => up("remit_city", e.target.value)} disabled={!canManage} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>State</label><input style={inp} value={form.remit_state || ""} onChange={e => up("remit_state", e.target.value)} disabled={!canManage} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Zip</label><input style={inp} value={form.remit_zip || ""} onChange={e => up("remit_zip", e.target.value)} disabled={!canManage} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>W9 status</label>
                <select style={inp} value={form.w9_status || "unknown_review"} onChange={e => up("w9_status", e.target.value)} disabled={!canManage}>
                  {W9_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Payment preference</label>
                <select style={inp} value={form.payment_preference || ""} onChange={e => up("payment_preference", e.target.value)} disabled={!canManage}>
                  {MECH_OPTS.map(v => <option key={v} value={v}>{v || "—"}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>TIN / EIN {tin4 ? <span style={{ color: "var(--text3,#888)", fontWeight: 400 }}>(on file ••••{tin4})</span> : ""}</label>
                <input style={inp} placeholder={tin4 ? "enter to replace" : "—"} value={form._tin || ""} onChange={e => up("_tin", e.target.value)} disabled={!canManage} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>1099 reportable</label>
                <select style={inp} value={form.reportable_1099 === true ? "yes" : form.reportable_1099 === false ? "no" : ""} onChange={e => up("reportable_1099", e.target.value === "yes" ? true : e.target.value === "no" ? false : null)} disabled={!canManage}>
                  <option value="">— undetermined —</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
            </div>
            <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={!!form.payment_hold} onChange={e => up("payment_hold", e.target.checked)} disabled={!canManage} /> Payment hold
            </label>
            {form.payment_hold && <input style={inp} placeholder="Hold reason" value={form.payment_hold_reason || ""} onChange={e => up("payment_hold_reason", e.target.value)} disabled={!canManage} />}
            <label style={lbl}>Payment instructions</label><textarea style={{ ...inp, minHeight: 44 }} value={form.payment_instructions || ""} onChange={e => up("payment_instructions", e.target.value)} disabled={!canManage} />

            <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: msg === "Saved." ? "#166534" : "#991b1b" }}>{msg}</span>
              {canManage && <button className="btn btn-blue" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save payee profile"}</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
